// Support Mainnet — non-translatable structural data.
//
// Translatable strings (names, roles, bios, titles, descriptions, questions,
// answers, mission/vision body, share text, hero paragraphs) live
// in `src/i18n/locales/en.json` under the `supportMainnet.*` namespace and
// are accessed via `useTranslations('supportMainnet')` in consumer
// components. This file holds only the structural metadata that should not
// be localized: ordering, ids/slugs, image paths, status enums, and the
// monetary goal/preset numbers.

export interface TeamMember {
  slug: TeamMemberSlug;
  imageSrc: string;
  xUrl?: string;
  xHandle?: string;
}

// Placeholder team slots are intentionally not listed publicly. Add a
// `TeamMember` entry here AND a matching `team.members.<slug>` block in
// `i18n/locales/en.json` when announcing a new team member.
export type TeamMemberSlug = 'cam' | 'lennart' | 'bohdan' | 'adam' | 'luca' | 'oleksii' | 'matt';

// The section renders as a pyramid: founders on the narrower top row, the rest
// on the wider base row. Splitting the list here (rather than slicing by index
// in the component) keeps the row a card belongs to a data decision.
export const TEAM_FOUNDERS: readonly TeamMember[] = [
  {
    slug: 'cam',
    imageSrc: '/images/support-mainnet/team/cam-v2.png',
    xUrl: 'https://x.com/itscammillen',
    xHandle: '@itscammillen',
  },
  {
    slug: 'lennart',
    imageSrc: '/images/support-mainnet/team/lennart-v2.png',
    xUrl: 'https://x.com/brandtnewlabs',
    xHandle: '@brandtnewlabs',
  },
  {
    slug: 'bohdan',
    imageSrc: '/images/support-mainnet/team/bohdan-v2.png',
    xUrl: 'https://x.com/bohdanshyker',
    xHandle: '@bohdanshyker',
  },
] as const;

export const TEAM_CREW: readonly TeamMember[] = [
  { slug: 'adam', imageSrc: '/images/support-mainnet/team/adam.png' },
  { slug: 'luca', imageSrc: '/images/support-mainnet/team/luca.png' },
  { slug: 'oleksii', imageSrc: '/images/support-mainnet/team/oleksii.png' },
  { slug: 'matt', imageSrc: '/images/support-mainnet/team/matt.png' },
] as const;

export const TEAM_MEMBERS: readonly TeamMember[] = [...TEAM_FOUNDERS, ...TEAM_CREW] as const;

export type TeamEmployerLogoId =
  | 'quizgecko'
  | 'cambridge'
  | 'solana'
  | 'state-street'
  | 'revolut'
  | 'fifa'
  | 'european-parliament';

export interface TeamEmployerLogo {
  id: TeamEmployerLogoId;
  label: string;
  /** Intrinsic SVG width at this logo's rendered height. */
  width: number;
  /**
   * Rendered height, when the 32px wordmark default is wrong. A stacked
   * lockup is ~1.4:1 rather than ~4.5:1, so at 32px it reads as a thumbnail
   * next to the wordmarks and its own wordmark falls below legible size.
   * Giving it more height is what balances the row optically.
   */
  heightPx?: 48;
}

export const TEAM_EMPLOYER_LOGOS: readonly TeamEmployerLogo[] = [
  { id: 'cambridge', label: 'University of Cambridge', width: 149 },
  { id: 'solana', label: 'Solana', width: 37 },
  { id: 'state-street', label: 'State Street', width: 117 },
  { id: 'quizgecko', label: 'Quizgecko', width: 147 },
  { id: 'revolut', label: 'Revolut', width: 145 },
  { id: 'fifa', label: 'FIFA', width: 98 },
  { id: 'european-parliament', label: 'European Parliament', width: 68, heightPx: 48 },
] as const;

export const TEAM_EMPLOYER_LOGO_HEIGHT_PX = 32;

export interface CommunityChannel {
  id: 'telegram';
  url: string;
}

export const X_COMMUNITY_URL = 'https://x.com/morfimarkets' as const;

export const COMMUNITY_CHANNELS: readonly CommunityChannel[] = [
  { id: 'telegram', url: 'https://t.me/morfimarkets' },
] as const;

export type RoadmapStatus = 'done' | 'in_progress' | 'next' | 'future';

export type RoadmapItemId = 'devnet-launch' | 'public-beta' | 'audit' | 'mainnet' | 'mobile';

export interface RoadmapItem {
  id: RoadmapItemId;
  status: RoadmapStatus;
}

export const ROADMAP_ITEMS: readonly RoadmapItem[] = [
  { id: 'devnet-launch', status: 'done' },
  { id: 'public-beta', status: 'in_progress' },
  { id: 'audit', status: 'next' },
  { id: 'mainnet', status: 'future' },
  { id: 'mobile', status: 'future' },
] as const;

export type FaqItemId =
  | 'what-is-morfi'
  | 'what-is-opinion-market'
  | 'how-markets-resolve'
  | 'not-gambling'
  | 'how-payouts-work'
  | 'how-creators-earn'
  | 'trading-fees'
  | 'no-crypto-needed'
  | 'which-blockchain'
  | 'vs-prediction-markets'
  | 'is-morfi-safe'
  | 'what-is-mainnet'
  | 'when-mainnet'
  | 'devnet-trades';

export interface FaqItem {
  id: FaqItemId;
  /** Optional deep-link to the matching docs page (appended to DOCS_URL),
   *  surfaced as a "Read our docs" link in the answer — same pattern the
   *  how-it-works modals use. Only the product-concept Q&As have a docs page. */
  docsPath?: string;
}

export const FAQ_ITEMS: readonly FaqItem[] = [
  // Brand / product Q&As for "About Morfi" SEO and the FAQPage rich-result schema.
  { id: 'what-is-morfi', docsPath: '/introduction' },
  { id: 'what-is-opinion-market', docsPath: '/concepts/opinion-markets' },
  // Product / AEO Q&As (MOR-755) — high-intent queries for featured snippets.
  { id: 'how-markets-resolve' },
  { id: 'not-gambling' },
  { id: 'how-payouts-work' },
  { id: 'how-creators-earn' },
  { id: 'trading-fees' },
  { id: 'no-crypto-needed' },
  { id: 'which-blockchain' },
  { id: 'vs-prediction-markets' },
  { id: 'is-morfi-safe' },
  // Launch logistics.
  { id: 'what-is-mainnet' },
  { id: 'when-mainnet' },
  { id: 'devnet-trades' },
] as const;

export const DONATE_GOAL_USD = 50000;
export const DONATE_PRESETS_USD = [5, 25, 100] as const;

// Per-currency fundraising goals. Each value is a hand-picked round number
// in its native currency that roughly approximates DONATE_GOAL_USD via late-
// 2025 exchange rates. The goal is a fundraising "vibes" anchor — exact
// USD parity isn't required, and viewers in different currencies see
// slightly different campaign sizes by design. Currencies not listed fall
// back to converting DONATE_GOAL_USD via the live exchange rate.
export const DONATE_GOAL_BY_CURRENCY: Readonly<Record<string, number>> = {
  USD: 50_000,
  EUR: 50_000,
  GBP: 40_000,
  JPY: 7_500_000,
  KRW: 70_000_000,
  CNY: 350_000,
  INR: 4_000_000,
  BRL: 250_000,
  CAD: 70_000,
  AUD: 75_000,
  CHF: 50_000,
  HKD: 400_000,
  SGD: 65_000,
  SEK: 500_000,
  NOK: 500_000,
  MXN: 850_000,
  NZD: 80_000,
  ZAR: 1_000_000, // ALLOW-D6-LITERAL: ZAR donation goal amount (1M Rand), not a D6 scale factor.
  TRY: 1_500_000,
  RUB: 5_000_000,
  PLN: 200_000,
  THB: 1_750_000,
  IDR: 750_000_000,
  VND: 1_250_000_000,
  TWD: 1_500_000,
  AED: 200_000,
  SAR: 200_000,
  PHP: 3_000_000,
  MYR: 250_000,
  CZK: 1_000_000, // ALLOW-D6-LITERAL: CZK donation goal amount (1M Koruna), not a D6 scale factor.
};

export function getDonationGoalLocal(currency: string, exchangeRate: number): number {
  const explicit = DONATE_GOAL_BY_CURRENCY[currency];
  if (explicit !== undefined) {
    return explicit;
  }
  return DONATE_GOAL_USD * exchangeRate;
}
